<script setup lang="ts">
  import Sheet from '@/components/Sheet.vue'
  import { Reorder, SwitchButton, type Column } from '@logistiek-zonder-papier/lzp-library'
  import { useTranslation } from 'i18next-vue'
  import { ref, watch } from 'vue'
  import { useRoute } from 'vue-router'

  type ColumnSetting = {
    key: string
    visible: boolean
  }

  const { columns, defaultColumns } = defineProps<{
    columns: Column[]
    defaultColumns?: string[]
  }>()

  const show = defineModel<boolean>('show', { required: true })
  const visibleColumns = defineModel<Column[]>('visibleColumns', { required: true })

  const { t: translate } = useTranslation()
  const route = useRoute()

  const settings = ref<ColumnSetting[]>([])

  watch(
    () => columns,
    columns => {
      if (columns.length === 0) return
      settings.value = mergeSettings(readStoredSettings(), columns)
    },
    { immediate: true }
  )

  watch(
    settings,
    settings => {
      if (settings.length > 0) localStorage.setItem(getStorageKey(), JSON.stringify(settings))
      visibleColumns.value = settings
        .filter(setting => setting.visible)
        .map(setting => findColumn(setting.key))
        .filter((column): column is Column => column !== undefined)
    },
    { deep: true, immediate: true }
  )

  function getStorageKey() {
    return `tableColumns:${route.matched.at(-1)?.path ?? route.path}`
  }

  function readStoredSettings(): ColumnSetting[] {
    try {
      const storedSettings = JSON.parse(localStorage.getItem(getStorageKey()) ?? '[]')
      return Array.isArray(storedSettings) ? storedSettings : []
    } catch {
      return []
    }
  }

  function mergeSettings(storedSettings: ColumnSetting[], columns: Column[]) {
    const settings = storedSettings.filter(setting =>
      columns.some(column => column.key === setting.key)
    )

    for (const column of columns) {
      if (settings.some(setting => setting.key === column.key)) continue
      settings.push({
        key: column.key,
        visible: defaultColumns?.includes(column.key) ?? true,
      })
    }

    return settings
  }

  function findColumn(key: string) {
    return columns.find(column => column.key === key)
  }
</script>

<template>
  <Sheet :is-open="show" title="Tabel aanpassen" @close="show = false">
    <div v-for="(setting, index) in settings" :key="setting.key" class="column-row">
      <span class="column-label">
        {{ translate(findColumn(setting.key)?.label ?? setting.key) }}
      </span>

      <div class="column-controls">
        <SwitchButton v-model="setting.visible" size="sm" />
        <Reorder v-model="settings" :index="index" />
      </div>
    </div>
  </Sheet>
</template>

<style scoped>
  .column-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--spacing-s);
    padding: var(--spacing-xs) 0;
    border-bottom: 1px solid var(--colours-grey-3);
  }

  .column-label {
    font-size: 0.875rem;
  }

  .column-controls {
    display: flex;
    align-items: center;
    gap: var(--spacing-s);
  }
</style>
